import { ErrorMessage } from "@hookform/error-message"
import { ActionButton, BaseTable, TableHeader } from "components"
import { APPROVAL } from "pages/Sales/Register/utils/vars"
import { useEffect, useRef, useState } from "react"
import {
  Controller,
  useFieldArray,
  useFormContext,
  useWatch
} from "react-hook-form"
import toast from "react-hot-toast"
import { FormTransferItemsType } from "../../../types"
import { useProductAvailableList, useWarehouseByLocation } from "../../../utils/hooks"
import { RowTransferItem } from "./RowTransferItem"
import { convertNumber } from "utils"

type TableCustomerOrderProps = {
  approvalView?: boolean
  display?: boolean
}

export const TableTransferItem = ({
  approvalView = false,
  display = true
}: TableCustomerOrderProps) => {
  const methods = useFormContext<FormTransferItemsType>()
  const [editField, setEditField] = useState<number | null>(null)
  const [location_from_id, approval_status] = useWatch({
    control: methods.control,
    name: ["location_from_id", "approval_status"]
  })
  const prevLocationFromId = useRef(location_from_id)
  const products = useProductAvailableList({ location_id: location_from_id })
  const warehouse = useWarehouseByLocation(location_from_id)

  const { fields, append, remove } = useFieldArray({
    control: methods.control,
    name: "transfer_detail"
  })

  const isDuplicateProductWarehouse = (
    value: { product_id?: number; location_warehouse_id?: number },
    ignoreIndex?: number
  ) => {
    return fields.some((item, index) => {
      if (typeof ignoreIndex === "number" && index === ignoreIndex) {
        return false
      }

      return (
        Number(item.product_id) === Number(value.product_id) &&
        Number(item.location_warehouse_id) === Number(value.location_warehouse_id)
      )
    })
  }
  const duplicateRowIndexes = fields.reduce((acc, item, index) => {
    const rowKey = `${Number(item.product_id)}-${Number(item.location_warehouse_id)}`
    const isValidKey =
      Number(item.product_id) > 0 && Number(item.location_warehouse_id) > 0

    if (!isValidKey) {
      return acc
    }

    const matchingRows = fields
      .map((fieldItem, fieldIndex) => ({ fieldItem, fieldIndex }))
      .filter(
        ({ fieldItem }) =>
          `${Number(fieldItem.product_id)}-${Number(fieldItem.location_warehouse_id)}` === rowKey
      )

    if (matchingRows.length > 1) {
      acc.add(index)
    }

    return acc
  }, new Set<number>())

  useEffect(() => {
    if (prevLocationFromId.current === location_from_id) {
      return
    }

    prevLocationFromId.current = location_from_id

    methods.getValues("transfer_detail").forEach((_, index) => {
      methods.setValue(`transfer_detail.${index}.location_warehouse_id`, NaN)
      methods.setValue(`transfer_detail.${index}.warehouse_name`, "")
    })
    // eslint-disable-next-line
  }, [location_from_id])


  return (
    <div className={`${display ? "" : "hidden"} overflow-x-auto`}>
      <BaseTable>
        <TableHeader className="uppercase">
          <tr>
            <th>-</th>
            <th>Item Code</th>
            <th>Description</th>
            <th>Warehouse</th>
            <th>SOH Source</th>
            <th>SOH Destination</th>
            <th>Unit</th>
            <th className="w-1/6">Ship</th>
            <th className={approvalView ? "hidden" : ""}>ACTION</th>
          </tr>
        </TableHeader>

        <tbody>
          {fields.map((arrField, key) => {
            return (
              <Controller
                key={arrField.id}
                control={methods.control}
                name={`transfer_detail.${key}`}
                render={({ field }) => {
                  if (editField === key) {
                    return (
                      <RowTransferItem
                        key={arrField.id}
                        products={products}
                        index={editField}
                        action="edit"
                        isHideForm={false}
                        defaultValues={field.value}
                        warehouseOptions={warehouse.data}
                        isWarehouseLoading={warehouse.isLoading}
                        onCancelEditing={() => setEditField(null)}
                        onSubmit={(value, rowMethods) => {
                          if (isDuplicateProductWarehouse(value, key)) {
                            toast.error("Product + warehouse is already in the list")
                            rowMethods?.setError(
                              "location_warehouse_id",
                              { message: "Product + warehouse is already in the list" },
                              { shouldFocus: true }
                            )
                            return
                          }

                          field.onChange(value)
                          setEditField(null)
                        }}
                      />
                    )
                  } else {
                    return (
                      <tr className={duplicateRowIndexes.has(key) ? "bg-red-50" : ""}>
                        <td className="text-center px-0">{key + 1}</td>
                        <td>{field.value.product_barcode}</td>
                        <td>{field.value.product_name}</td>
                        <td>
                          {field.value.warehouse_name ??
                            warehouse.data.find(
                              (item) =>
                                Number(item.value) === field.value.location_warehouse_id
                            )?.label ??
                            "-"}
                          {duplicateRowIndexes.has(key) && (
                            <p className="text-error text-xs mt-1">
                              Duplicate item + warehouse
                            </p>
                          )}
                        </td>
                        <td className="text-right">{field.value.soh}</td>
                        <td className="text-right">{field.value.soh_destination ? convertNumber(field.value.soh_destination).intoNormalAmount : ''}</td>
                        <td>{field.value.mr_unit_name}</td>
                        <td className="text-right">
                          {field.value.quantity_transfer}
                        </td>
                        <td className={approvalView ? "hidden" : ""}>
                          {methods.getValues("approval_status") !==
                            APPROVAL.APPROVED && (
                              <div className="flex justify-center items-center">
                                <ActionButton.Update
                                  onClick={() => setEditField(key)}
                                />

                                <ActionButton.Delete
                                  onClick={() => remove(key)}
                                />
                              </div>
                            )}
                        </td>
                      </tr>
                    )
                  }
                }}
              />
            )
          })}

          {approval_status === APPROVAL.PENDING && !approvalView && (
            <RowTransferItem
              key={fields.length}
              products={products}
              warehouseOptions={warehouse.data}
              isWarehouseLoading={warehouse.isLoading}
              action="add"
              onSubmit={(value, methods) => {
                if (!isDuplicateProductWarehouse(value)) {
                  return append(value)
                }

                toast.error("Product + warehouse is already in the list")

                methods?.setError(
                  "location_warehouse_id",
                  { message: "Product + warehouse is already in the list" },
                  { shouldFocus: true }
                )
              }}
            />
          )}
        </tbody>
      </BaseTable>

      <ErrorMessage
        errors={methods.formState.errors}
        name="sodetail"
        render={({ message }) => (
          <div className="mt-2">
            <p className="text-red-500">{message}</p>
          </div>
        )}
      />
    </div>
  )
}
